/**
 * Created by Bowen on 6/14/2020.
 * Description: contact body detail service
 *
 * ------ maintenance history ------
 * 2020/7/13 Bowen Add the common function for contact profile info
 */

import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { Contact } from '../../tamalelibs/models/contact.model';
import { DatePipe } from '@angular/common';
import { ContactInformationItemType } from './contact-detail-template/contact-information-item/contact-information-item.model';
import { ContactDialogSectionName } from './contact-body-detail.model';
import { ArrayHelperService } from '../../tamalelibs/services/array-helper.service';

@Injectable({
    providedIn: 'root',
})
export class ContactDetailService {
    action$: Subject<any> = new Subject<any>();
    feedback$: Subject<any> = new Subject<any>();

    constructor(
        private _datepipe: DatePipe,
    ) {
    }

    // get contact address
    getContactAddress(currentContact: Contact) {
        const contactAddress = [];
        if (currentContact.webAddress && currentContact.webAddress.length > 0) {
            currentContact.webAddress.forEach(webAddressItem => {
                if (webAddressItem.originalAddress && !webAddressItem.country && !webAddressItem.state && !webAddressItem.street) {
                    contactAddress.push({
                        title: webAddressItem.name + ' Address',
                        value: [{
                            clickable: false,
                            value: webAddressItem.originalAddress,
                        }]
                    });
                } else {
                    const webAddressValue = [webAddressItem.street2, webAddressItem.street, webAddressItem.city, webAddressItem.state, webAddressItem.country, webAddressItem.zip].filter(item => !!item).join(', ');
                    if (webAddressValue) {
                        contactAddress.push({
                            title: webAddressItem.name + ' Address',
                            value: [{
                                clickable: false,
                                value: webAddressValue,
                            }]
                        });
                    }
                }
            });
        }
        return contactAddress;
    }

    getContactBasicInformation(currentContact: Contact, templateSetting?: any) {
        const basicInfo = [];
        if (currentContact) {
            // for the name, at lease, we need to show the first name
            // and then will show the prefix, middle name, last name, suffix according to the template setting
            // prefix, suffix must show in the basic information
            const prefix = currentContact.attribute && currentContact.attribute.prefix && templateSetting && templateSetting.Prefix ? currentContact.attribute.prefix + ' ' : '';
            const middleName = currentContact.attribute && currentContact.attribute.middleName && templateSetting && templateSetting['Middle Name'] ? currentContact.attribute.middleName + ' ' : '';
            const lastName = currentContact.lastName ? currentContact.lastName + ' ' : '';
            const suffix = currentContact.attribute && currentContact.attribute.suffix && templateSetting && templateSetting.Suffix ? currentContact.attribute.suffix + ' ' : '';
            basicInfo.push({
                title: 'Full Name',
                value: [{
                    clickable: false,
                    value: prefix + currentContact.firstName + ' ' + middleName + lastName + suffix,
                }]
            });

            // first, need has the gender value in the template
            // second, the template setting has set the gender show in the basic information part
            if (currentContact.attribute && currentContact.attribute.gender && templateSetting && templateSetting.Gender && templateSetting.Gender === ContactDialogSectionName.BasicInformation) {
                basicInfo.push({
                    title: 'Gender',
                    value: [{
                        clickable: false,
                        value: currentContact.attribute.gender,
                    }]
                });
            }

            // first, need has the birthday value in the template
            // second, the template setting has set the birthday show in the basic information part
            if (currentContact.birthday && templateSetting && templateSetting.Birthday === ContactDialogSectionName.BasicInformation) {
                basicInfo.push({
                    title: 'Birthday',
                    value: [{
                        clickable: false,
                        value: this._datepipe.transform(currentContact.birthday, 'MM/dd/yyyy'),
                    }]
                });
            }

            if (currentContact.aliases && currentContact.aliases.length > 0) {
                basicInfo.push({
                    title: 'Alias',
                    value: [{
                        clickable: false,
                        value: currentContact.aliases.join(', '),
                    }]
                });
            }
        }
        return basicInfo;
    }

    getContactConnectDetail(currentContact: Contact) {
        const contactInfo = [];
        if (currentContact.webEmail) {
            currentContact.webEmail.forEach(element => {
                if (element.address) {
                    contactInfo.push({
                        title: element.name + ' Email',
                        value: [{
                            clickable: true,
                            value: element.address,
                            itemType: ContactInformationItemType.Email
                        }]
                    });
                }
            });
        }

        if (currentContact.webPhone) {
            currentContact.webPhone.forEach(element => {
                if (element.phone) {
                    contactInfo.push({
                        title: element.name + ' Phone',
                        value: [{
                            clickable: false,
                            value: element.phone,
                        }]
                    });
                }
            });
        }
        return contactInfo;
    }

    // get contact social media
    getContactSocialMedia(currentContact: Contact) {
        const internet = [];
        currentContact.socialMedia.forEach(element => {
            const re = /^(((https|http|ftp|rtsp|mms)?:\/\/)|(www\.))/;
            if (element.link) {
                if (re.test(element.link)) {
                    internet.push({
                        title: element.name,
                        value: [{
                            clickable: true,
                            value: element.link,
                            itemType: ContactInformationItemType.Link
                        }]
                    });
                } else {
                    internet.push({
                        title: element.name,
                        value: [{
                            clickable: false,
                            value: element.link
                        }]
                    });
                }
            }
        });
        return internet;
    }

    // get contact current and former employment
    getContactEmploymentDetail(currentContact: Contact) {
        let currentEmployment = [];
        let formerEmployment = [];
        if (currentContact.relationships) {
            currentEmployment = currentContact.relationships.filter(currentItem => currentItem.nodesType === 'currentEmployment');
            ArrayHelperService.sort(currentEmployment, 'startYear');

            formerEmployment = currentContact.relationships.filter(formerItem => formerItem.nodesType === 'formerEmployment')
                .sort((a, b) => {
                    if (b.endYear === a.endYear) {
                        const alow = a.startYear;
                        const blow = b.startYear;
                        return alow === blow ? 0 : (alow < blow ? 1 : -1);
                    } else {
                        const alow = a.endYear;
                        const blow = b.endYear;
                        return alow === blow ? 0 : (alow < blow ? 1 : -1);
                    }
                });
        }
        let currentEmploymentInfo = [];
        let formerEmploymentInfo = [];
        let employmentInfo = [];
        // to save 5 values: relationship id, child entity id, realtionship type name, relationship id, child entity name
        const _currentEmployment = [];
        const _formerEmployment = [];
        if (currentEmployment.length > 0) {
            currentEmployment.forEach(currentItem => {
                _currentEmployment.push({
                    title: 'Company Name',
                    value: [{
                        value: currentItem.childEntity.name,
                        valueId: currentItem.childEntity.id,
                        clickable: true,
                        itemType: ContactInformationItemType.Entity
                    }]
                });
                _currentEmployment.push({
                    title: 'Job Title',
                    value: [{
                        value: currentItem.relationshipType.name,
                        valueId: currentItem.relationshipType.id,
                        clickable: false,
                    }]
                });
                if (this._parseTimeToStandardTime(currentItem.startYear) !== 0) {
                    _currentEmployment.push({
                        title: 'Period',
                        value: [{
                            value: this._parseTimeToStandardTime(currentItem.startYear) + ' - Present',
                            clickable: false,
                        }]
                    });
                }
                _currentEmployment.push({
                    title: 'item-divider'
                });
            });
        } else {
            // if the current employment doesn't exist, we need to show the company or job title
            // this is the company info
            if (currentContact.companyWeb && currentContact.companyWeb.id && currentContact.companyWeb.name) {
                _currentEmployment.push({
                    title: 'Company Name',
                    value: [{
                        value: currentContact.companyWeb.name,
                        valueId: currentContact.companyWeb.id,
                        clickable: true,
                        itemType: ContactInformationItemType.Entity
                    }]
                });
            }
            // this is the job title info
            if (currentContact.jobTitleWeb.filter(item => item.id).length > 0) {
                _currentEmployment.push({
                    title: 'Job Title',
                    value: [{
                        value: currentContact.jobTitleWeb.filter(item => item.id).map(res => res.name).join(', '),
                        valueId: '',
                        clickable: false,
                    }]
                });
            }
            // this is the divider info
            if (_currentEmployment.length > 0) {
                _currentEmployment.push({
                    title: 'item-divider'
                });
            }
        }

        if (formerEmployment.length > 0) {
            formerEmployment.forEach(formerItem => {
                let _period = '';
                _formerEmployment.push({
                    title: 'Company Name',
                    value: [{
                        value: formerItem.childEntity.name,
                        valueId: formerItem.childEntity.id,
                        clickable: true,
                        itemType: ContactInformationItemType.Entity
                    }]
                });
                _formerEmployment.push({
                    title: 'Title',
                    value: [{
                        value: formerItem.relationshipType.name,
                        valueId: formerItem.relationshipType.id,
                        clickable: false,
                    }]
                });
                if (this._parseTimeToStandardTime(formerItem.startYear) === 0) {
                    _period = '-' + this._parseTimeToStandardTime(formerItem.endYear);
                } else {
                    _period = this._parseTimeToStandardTime(formerItem.startYear) + ' - ' + this._parseTimeToStandardTime(formerItem.endYear);
                }
                _formerEmployment.push({
                    title: 'Period',
                    value: [{
                        value: _period,
                        clickable: false,
                    }]
                });
                _formerEmployment.push({
                    title: 'item-divider'
                });
            });
        }

        // the last current employment and former employment should not include the divider
        _currentEmployment.pop();
        _formerEmployment.sort((a, b) => {
            const alow = a.endYear;
            const blow = b.endYear;
            return alow === blow ? 0 : (alow < blow ? 1 : -1);
        }).pop();

        currentEmploymentInfo = _currentEmployment;
        formerEmploymentInfo = _formerEmployment;
        if (currentEmploymentInfo.length > 0 && formerEmploymentInfo.length > 0) {
            employmentInfo = currentEmploymentInfo.concat([{ title: 'item-divider' }]).concat(formerEmploymentInfo);
        } else if (currentEmploymentInfo.length > 0 && formerEmploymentInfo.length === 0) {
            employmentInfo = currentEmploymentInfo;
        } else if (currentEmploymentInfo.length === 0 && formerEmploymentInfo.length > 0) {
            employmentInfo = formerEmploymentInfo;
        } else {
            employmentInfo = [];
        }
        return {
            currentEmploymentInfo: currentEmploymentInfo,
            formerEmploymentInfo: formerEmploymentInfo,
            employmentInfo: employmentInfo, // this var is for relationship tile - employment tab
        };
    }

    getContactOtherInformation(currentContact: Contact, templateSetting?: any) {
        const otherInfo = [];
        if (currentContact.birthday && templateSetting && templateSetting.Birthday === ContactDialogSectionName.Other) {
            otherInfo.push({
                title: 'Birthday',
                value: [{
                    clickable: false,
                    value: this._datepipe.transform(currentContact.birthday, 'MM/dd/yyyy'),
                }]
            });
        }

        if (currentContact.attribute && currentContact.attribute.gender && templateSetting && templateSetting.Gender === ContactDialogSectionName.Other) {
            otherInfo.push({
                title: 'Gender',
                value: [{
                    clickable: false,
                    value: currentContact.attribute.gender,
                }]
            });
        }
        return otherInfo;
    }

    private _parseTimeToStandardTime(milTime) {
        let date;
        if (milTime === 0) {
            date = 0;
        } else {
            date = new Date(milTime).getFullYear();
        }
        return date;
    }
}
